<template>

	<div ref="wrapper" class="steps-form-wrapper">
		<div v-if="hasPermissions" class="logout-row">
			<AppClickableIcon
				icon-name="iconLogout"
				@click-icon="goToLogin"
			/>
		</div>
		<div v-if="hasPermissions" class="steps-form-content">
			<div class="mb-10">
				<h3 class="mb-5">{{getString('business_infos_popup_title')}}</h3>
				<p class="business-info-caption">{{getString('business_infos_caption')}}</p>
			</div>
			<CompanyInfoCollector disallow-other-countries ref="company-info" @changed-company="changedCompany" />
			<div class="row centered flex-end mt-20 buttons-row">
				<AppButton v-show="dismissable" :disabled="!!intervalDismissablePopup" outline :paused="loading || loadingVat" small :text="dismissText" @click="dismissBusinessInfos" />
				<AppButton :paused="loading || loadingVat" :disabled="!canSaveInfos" small :text="getString('save')" @click="saveBusinessInfos" />
			</div>
		</div>
		<div v-if="!hasPermissions" class="steps-form-content pr-10 pl-10">
			<p>{{ getString('business_setup_permission') }}</p>
			<div class="row centered flex-end pt-10">
				<AppButton small outline :text="getString('change_account')" :loading="loggingOut" @click="goToLogin" />
			</div>
		</div>
		<AppPopup
			:title="getString('same_vat_fiscal_error_title')"
			:body-text="getString('same_vat_fiscal_error_text', [this.company?.vat])"
			:buttons="popupVatFiscalButtons"
			ref="confirm-same-vat-fiscal"
		/>
	</div>

</template>

<script>
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";

import {useUserStore, useMenuStore} from "@/console-new/stores";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import API from "@/shared/helpers/API";
import {validateEmail} from "@/shared/helpers/strings";
import CompanyInfoCollector from "@/console-new/components/shared/CompanyInfoCollector.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import {getLocalDateFromServer} from "@/shared/helpers/dates";

export default {
	name: "BusinessInfosPopup",
	setup() {
		let userStore = useUserStore();
		let menuStore = useMenuStore();
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		return {validateEmail, getLocalDateFromServer, getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, menuStore};
	},
	components: {AppPopup, CompanyInfoCollector, AppGoogleMapsInput, AppLoader, AppTextInput, AppButton, AppClickableIcon},
	data() {
		return {
			loading: false,
			loadingVat: false,
			dismissedSameVatFiscal: false,
			intervalDismissablePopup: null,
			remainingWaitSeconds: 5,
			loggingOut: false,
			company: {
				vat: null,
				fiscal_name: null,
				fiscal_code: null,
				legal_address: null,
				delivery_address: null,
				sdi: null,
				pec: null,
				notes: null,
			},
		}
	},
	computed: {
		dismissText() {
			if(this.intervalDismissablePopup) {
				return this.getString('continue_in_x', [this.remainingWaitSeconds.toString()]);
			}
			return this.getString('do_it_later');
		},
		popupVatFiscalButtons() {
			return [
				{text: this.getString('edit'), callback: null, negative: true},
				{text: this.getString('confirm'), callback: this.confirmSameVatFiscal},
			]
		},
		canSaveInfos() {
			return this.userStore.currBusiness &&
				this.company.vat && this.company.vat.length > 8 && this.company.vat.length < 13 &&
				this.company.fiscal_name && this.company.fiscal_name.length > 3 && this.company.fiscal_name.length < 255 &&
				(!this.company.fiscal_code || (this.company.fiscal_code.length && this.company.fiscal_code.length > 5 && this.company.fiscal_code.length < 20))
		},
		hasPermissions() {
			if(!this.userStore || !this.userStore.currBusiness || !this.userStore.currBusiness.permissions || !this.userStore.currBusiness.permissions.length) {
				return true;
			}
			let finalPermissions = this.userStore.currBusiness.permissions;
			return finalPermissions.includes('admin');
		},
		dismissable() {
			//if never used trial, popup can be dismissed
			let neverUsedTrial = this.userStore?.user?.stripe_trial !== 1 && !this.userStore?.currBusiness?.stripe_trial;
			if(neverUsedTrial) {
				return true;
			}
			if(this.userStore?.currBusiness?.plan_trial_end) {
				return new Date() < this.getLocalDateFromServer(this.userStore.currBusiness.plan_trial_end)
			}
			return false;
		},
	},
	methods: {
		confirmSameVatFiscal() {
			this.dismissedSameVatFiscal = true;
			this.$refs['confirm-same-vat-fiscal'].toggleStatus(false);
			this.saveBusinessInfos();
		},
		changedCompany(company) {
			this.company = company;
			if(this.company.legal_address) {
				this.changeBusinessLegalAddress()
			}
			if(this.company.delivery_address) {
				this.changeBusinessAddress()
			}
		},
		goToLogin() {
			this.loggingOut = true;
			this.userStore.logout(() => {
				this.loggingOut = false;
			});
		},
		changeBusinessLegalAddress() {
			if(!this.company?.legal_address) {
				return
			}
			let finalAddress = Object.assign({}, this.company.legal_address, this.company.legal_address.data);
			delete finalAddress.data;
			this.company.legal_address = this.userStore.normalizeAddress(finalAddress);
			this.company.legal_address.address_id = null;
			this.legalAddressChanged = true;
		},
		changeBusinessAddress() {
			if(!this.company?.delivery_address) {
				return
			}
			let finalAddress = Object.assign({}, this.company.delivery_address, this.company.delivery_address.data);
			delete finalAddress.data;
			this.company.delivery_address = this.userStore.normalizeAddress(finalAddress);
			this.company.delivery_address.address_id = null;
			this.addressChanged = true;
		},
		saveBusinessAddress() {
			if(this.addressChanged && this.company.delivery_address) {
				API.init().addNewAddress(this.userStore.currBusinessId, this.company.delivery_address.address, this.company.delivery_address.lat, this.company.delivery_address.lng, JSON.stringify(this.company.delivery_address), "executive", response => {
					if (response && response.result === 'OK') {
						if(response.address_id) {
							this.userStore.currBusiness.address_id = Number(response.address_id);
							this.company.delivery_address.address_id = Number(response.address_id);
							this.userStore.currBusiness.address = this.company.delivery_address;
						}
						this.showNotification('saved-business-address');
					} else {
						this.showNotification('error-address');
					}
					this.addressChanged = false;
				});
			}
			if(this.legalAddressChanged && this.company.legal_address) {
				API.init().addNewAddress(this.userStore.currBusinessId, this.company.legal_address.address, this.company.legal_address.lat, this.company.legal_address.lng, JSON.stringify(this.company.legal_address), "legal", response => {
					if (response && response.result === 'OK') {
						if(response.address_id) {
							this.userStore.currBusiness.legal_address_id = Number(response.address_id);
							this.company.legal_address.address_id = Number(response.address_id);
							this.userStore.currBusiness.legal_address = this.company.legal_address;
						}
						this.showNotification('saved-business-address');
					} else {
						this.showNotification('error-address');
					}
					this.legalAddressChanged = false;
				});
			}
		},
		saveFiscalData() {
			let dataUpdate = {};
			let shouldUpdate = false;

			if (!this.company.vat ||
				!this.company.fiscal_name
			) {
				this.showNotification('error-fill-fields');
				return;
			}

			if(this.company.vat && this.company.vat.length > 8 && this.company.vat.length < 13) {
				dataUpdate.vat = this.company.vat;
				shouldUpdate = true;
			}
			if(this.company.fiscal_name && this.company.fiscal_name.length > 3 && this.company.fiscal_name.length < 255) {
				dataUpdate.fiscal_name = this.company.fiscal_name;
				shouldUpdate = true;
			}
			if(!this.company.fiscal_code ||
				(this.company.fiscal_code.length && this.company.fiscal_code.length > 5 && this.company.fiscal_code.length < 20)) {
				dataUpdate.fiscal_code = this.company?.fiscal_code || null;
				shouldUpdate = true;
			}
			if(this.company.sdi && this.company.sdi.length > 4 && this.company.sdi.length < 10) {
				dataUpdate.sdi = this.company.sdi;
				shouldUpdate = true;
			}
			if(!shouldUpdate) {
				return;
			}
			this.loading = true;
			API.init().updateBusiness(this.userStore.currBusinessId, dataUpdate, response => {
				this.loading = false;
				if (response && response.result === 'OK') {
					this.showNotification('saved-pec');
					if(dataUpdate.sdi) { this.userStore.currBusiness.sdi = dataUpdate.sdi }
					if(dataUpdate.vat) { this.userStore.currBusiness.vat = dataUpdate.vat }
					if(dataUpdate.fiscal_name) { this.userStore.currBusiness.fiscal_name = dataUpdate.fiscal_name }
					if(dataUpdate.hasOwnProperty('fiscal_code')) { this.userStore.currBusiness.fiscal_code = dataUpdate.fiscal_code }
					this.dismissedSameVatFiscal = false;
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		dismissBusinessInfos() {
			this.userStore.dismissedFiscalPopup = true;
		},
		saveBusinessInfos() {

			if(!this.dismissedSameVatFiscal && this.company?.vat && this.company?.fiscal_code && this.company.vat === this.company.fiscal_code) {
				this.$refs['confirm-same-vat-fiscal'].toggleStatus(true);
				return;
			}
			this.dismissedSameVatFiscal = false;
			this.saveFiscalData();
			this.saveBusinessAddress();
		},
		startDismissTimer() {
			this.intervalDismissablePopup = setInterval(() => {
				if(this.remainingWaitSeconds > 1) {
					this.remainingWaitSeconds -= 1;
				} else {
					clearInterval(this.intervalDismissablePopup);
					this.intervalDismissablePopup = null;
				}
			}, 1000);
		},
	},
	mounted() {
		if(this.$refs?.['company-info']?.resetCompany) {
			this.$refs['company-info'].resetCompany();
		}
		if(!this.hasPermissions) {
			this.$router.push('/lost');
		}
		this.startDismissTimer();
	},
	watch: {
		'$route.path'(newVal) {
			if(newVal !== '/lost' && !this.hasPermissions) {
				this.$router.push('/lost');
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.steps-form-wrapper {
	@include centered;
	position: fixed;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	padding: 5px;
	background-color: var(--color-bg-overlay);
	backdrop-filter: blur(5px);
	-webkit-backdrop-filter: blur(5px);
	z-index: 200;
	max-width: 100vw;
	max-height: 100vh;
	overflow-y: auto;

	.logout-row {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		position: absolute;
		top: 0;
		right: 0;
		width: 100%;
		padding: 20px;
	}

	.steps-form-content {
		width: 100%;
		max-width: 600px;
		border: 1px solid var(--color-primary);
		border-radius: 26px;
		padding: 20px;
		background-color: var(--color-bg);
		max-height: calc(100vh - 130px);
		overflow-y: auto;
		margin-bottom: 50px;

		.business-info-caption {
			font-size: 16px;
		}
	}


	.input-container {
		position: relative;
	}
	p.caption {
		font-size: 20px;
		color: #9CA3AF;
		margin-bottom: 20px;
	}

	@media (max-width: 920px) {

		p.caption {
			font-size: 16px;
		}
	}

}
.input-button-row {
	@include centered;
	justify-content: space-between;

	&.vat-row {
		padding: 10px;
		background-color: var(--color-alpha-1);
		border-radius: 13px;
		overflow: hidden;
	}

	.input-wrapper, .select-wrapper {
		&:not(.single-input-wrapper) {
			margin-right: 20px;
		}
	}
	.add-user-input {
		margin-right: 0;
	}
	> p {
		font-size: 16px;
		max-width: calc(100% - 410px);
	}
	&.select-row > p {
		font-size: 16px;
	}
	.input-btn-container {
		min-width: 410px;
	}
	@media (max-width: 768px) {
		&:not(.select-row) {
			justify-content: flex-end;

			.row:not(.vat-loader-row) {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.input-wrapper, .select-wrapper {
				margin-right: 0;
				max-width: 100%;
				&:not(.single-input-wrapper) {
					margin-bottom: 10px;
				}
			}
			.select-wrapper{ width: 100%; }
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
	@media (max-width: 600px) {
		> p, &.select-row > p {
			font-size: 14px;
		}
		.input-btn-container {
			min-width: unset;
			.select-wrapper{ width: auto; }
		}
		> p {
			max-width: calc(100% - 310px);
		}
	}
	@media (max-width: 480px) {
		&.select-row {
			align-items: flex-start;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				margin-right: 0;
				margin-bottom: 10px;
				max-width: unset;
				min-width: 200px;
			}
			> p {
				max-width: calc(100% - 180px);
				margin-bottom: 4px;
			}
		}

		.input-btn-container {
			min-width: 100%;
		}
	}
	@media (max-width: 400px) {
		> p, &.select-row > p {
			font-size: 12px;
		}
		.vat-loader-row {
			justify-content: flex-start;

			p {
				margin-right: 10px;
			}
		}
	}
	@media (max-width: 360px) {
		&.select-row {
			justify-content: flex-end;
			.row {
				flex-direction: column;
				align-items: flex-end;
				justify-content: center;
			}
			.select-wrapper {
				max-width: 100%;
				width: 100%;
			}
			> p {
				max-width: 100%;
				width: 100%;
				margin-bottom: 4px;
			}
		}
	}
}
.business-info-container > .row {
	position: relative;
	padding: 6px 10px;

	&:not(:last-of-type):after {
		@include pseudoLine(1px, 100%, 100%, 0, $alpha_4);
	}
	&:first-of-type {
		padding-top: 10px;
	}
	&:last-of-type {
		padding-bottom: 0;
	}
}
.buttons-row {
	gap: 20px;
}
</style>
